import { useState } from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faCircleQuestion,
  faThumbsDown,
  faThumbsUp,
} from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useListingDecisions } from '../../_hooks/use-listing-decisions'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { requestTagSuggestion } from '../../_data/renter-llm'
import { TagSuggestionResult } from '../../_types/quickpass-llm'
import {
  Wrap,
  Eyebrow,
  TopRow,
  Reasoning,
  Actions,
  PrimaryButton,
  TextButton,
  Pill,
  ErrorLine,
  StatusText,
} from './styled'
import { SuggestionState, TagSuggestionProps } from './types'

const SUGGESTION_LABEL: Record<TagSuggestionResult['suggestion'], string> = {
  yes: 'Yes',
  maybe: 'Maybe',
  no: 'No',
}

const SUGGESTION_ICON: Record<
  TagSuggestionResult['suggestion'],
  IconDefinition
> = {
  yes: faThumbsUp,
  maybe: faCircleQuestion,
  no: faThumbsDown,
}

/**
 * AI suggestion surface that recommends a yes/maybe/no tag with one
 * sentence of reasoning. Hidden once the renter has tagged the listing
 * themselves — assumes their hand-tag is canonical.
 *
 * @param {TagSuggestionProps} props
 * @returns {JSX.Element | null}
 */
const TagSuggestion = ({ listing }: TagSuggestionProps): JSX.Element | null => {
  const { profile } = useQuickPassProfile()
  const { tags, setListingTag } = useListingDecisions()
  const currentTag = tags[listing.id] ?? null

  const [state, setState] = useState<SuggestionState>('idle')
  const [result, setResult] = useState<TagSuggestionResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')

  if (currentTag !== null) {
    return null
  }

  const handleGenerate = async (): Promise<void> => {
    setState('loading')
    setErrorMessage('')

    try {
      const response = await requestTagSuggestion({
        listing,
        preferences: profile?.unitPreferences ?? null,
        takeHome: profile?.monthlyTakeHomeIncome ?? null,
      })

      if (response === null) {
        setState('offline')
        return
      }

      setResult(response)
      setState('ready')
    } catch (error) {
      setState('error')
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not generate a suggestion. Try again in a sec.',
      )
    }
  }

  const handleAccept = (): void => {
    if (result === null) {
      return
    }
    setListingTag(listing.id, result.suggestion)
  }

  const handleDismiss = (): void => {
    setState('idle')
    setResult(null)
  }

  return (
    <Wrap>
      <Eyebrow>QuickPass AI · Tag suggestion</Eyebrow>

      {state === 'idle' ? (
        <TopRow>
          <Reasoning>
            Let the AI suggest a tag for this listing based on your preferences.
            One click to accept.
          </Reasoning>
          <PrimaryButton type="button" onClick={handleGenerate}>
            Suggest a tag
          </PrimaryButton>
        </TopRow>
      ) : null}

      {state === 'loading' ? <StatusText>Thinking it over…</StatusText> : null}

      {state === 'offline' ? (
        <StatusText>Backend not configured. AI tag is offline.</StatusText>
      ) : null}

      {state === 'error' ? (
        <>
          <ErrorLine>{errorMessage}</ErrorLine>
          <Actions>
            <PrimaryButton type="button" onClick={handleGenerate}>
              Try again
            </PrimaryButton>
            <TextButton type="button" onClick={handleDismiss}>
              Dismiss
            </TextButton>
          </Actions>
        </>
      ) : null}

      {state === 'ready' && result !== null ? (
        <>
          <TopRow>
            <Pill suggestion={result.suggestion}>
              Suggested · <Icon icon={SUGGESTION_ICON[result.suggestion]} />{' '}
              {SUGGESTION_LABEL[result.suggestion]}
            </Pill>
            <Actions>
              <PrimaryButton type="button" onClick={handleAccept}>
                Apply tag
              </PrimaryButton>
              <TextButton type="button" onClick={handleDismiss}>
                Dismiss
              </TextButton>
            </Actions>
          </TopRow>
          <Reasoning>{result.reasoning}</Reasoning>
        </>
      ) : null}
    </Wrap>
  )
}

export default TagSuggestion
